// This comment tells ESLint that __webpack_public_path__ is a global variable so it doesn't complain about it
/* global __webpack_public_path__ */
import type { Job, JobOccupancy } from "@eai-toolkit/client";
import {
  DataApi,
  JobApi,
  JobOccupancyApi,
  MeApi,
  q,
} from "@eai-toolkit/client";
import axios from "axios";
import {
  format,
  formatDistanceStrict,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from "date-fns";
import numeral from "numeral";

let basePath = __webpack_public_path__.includes("://")
  ? __webpack_public_path__
  : `${window.location.origin}${__webpack_public_path__}`;
if (basePath.slice(-1) === "/") {
  basePath = basePath.slice(0, -1);
}

const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
  // [::1] is the IPv6 localhost address.
  window.location.hostname === "[::1]" ||
  // 127.0.0.1/8 is considered localhost for IPv4.
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/,
  ),
);

// Create a new Axios instance with the desired configuration
const axiosInstance = axios.create({
  baseURL: basePath + "/webapp",
  withCredentials: !isLocalhost,
});

const meApi = new MeApi(undefined, undefined, axiosInstance);
const jobApi = new JobApi(undefined, undefined, axiosInstance);
const jobOccupancyApi = new JobOccupancyApi(
  undefined,
  undefined,
  axiosInstance,
);
const dataApi = new DataApi(undefined, undefined, axiosInstance);
function getDateQueryParams(dateRange: "today" | "thisWeek") {
  const endDate = new Date();
  let startDate: Date;

  // Set start date based on dateRange
  if (dateRange === "today") {
    startDate = new Date(endDate);
    startDate.setHours(0, 0, 0, 0); // Start of today
  } else {
    // thisWeek
    startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 7); // 7 days ago
  }

  const startDateISO = startDate.toISOString();
  const endDateISO = endDate.toISOString();

  return q({
    fieldName: "created",
    operator: "between",
    value: [startDateISO, endDateISO],
  });
}

function getDateQueryParamsForData(dateRange: "today" | "thisWeek") {
  const endDate = new Date();
  let startDate: Date;

  // Set start date based on dateRange
  if (dateRange === "today") {
    startDate = new Date(endDate);
    startDate.setHours(0, 0, 0, 0); // Start of today
  } else {
    // thisWeek
    startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 7); // 7 days ago
  }

  const startDateISO = startDate.toISOString();
  const endDateISO = endDate.toISOString();

  return `created__between%3D${startDateISO},${endDateISO}`;
}

export async function getJobs({
  by,
  dateRange,
}: {
  by: "me" | "cluster";
  dateRange?: "today" | "thisWeek";
}) {
  let items: Job[];
  let queryParams: any = {
    limit: 100,
    human: true,
    order: "-created",
  };

  if (dateRange) {
    queryParams.q = getDateQueryParams(dateRange);
  }

  switch (by) {
    case "me": {
      const { data: userJobs } = await jobApi.v1MeJobGet({
        ...queryParams,
        human: true,
      });
      items = userJobs.items || [];
      break;
    }
    case "cluster": {
      const { data: clusterJobs } = await jobApi.v1ClusterJobGet(queryParams);
      items = clusterJobs.items || [];
      break;
    }
  }
  return items;
}

export type Stat = {
  title: string;
  subtitle: string;
  titleSuffix?: string;
  tooltip: string;
};

export enum MonitoringWindow {
  YEAR = "year",
  MONTH = "month",
  WEEK = "week",
}

const formatDate = (date: Date) => format(date, "MMMM do, u");

function createWindowString(dateA: string, dateB: string) {
  return [dateA, dateB].join(" – ");
}

export async function getData({
  by,
  dateRange,
}: {
  by: "me" | "cluster";
  dateRange?: "today" | "thisWeek";
}) {
  let queryParams: any = {
    limit: 100,
    human: true,
    order: "-created",
  };

  const queries = [];

  if (dateRange) {
    queries.push(getDateQueryParamsForData(dateRange));
  }

  if (by === "me") {
    const me = await meApi.v1MeGet();
    queries.push(
      q({
        fieldName: "creator",
        operator: "eq" as const,
        value: me.data.id,
      }),
    );
  }

  if (queries.length > 0) {
    queryParams.q = queries.join("&");
  }

  const { data } = await dataApi.v1ClusterDataGet(queryParams);
  return data.items || [];
}

export async function getUsage(
  window: MonitoringWindow,
): Promise<{ statistics: Stat[]; windowString: string }> {
  const LIMIT = 1024;
  let items: JobOccupancy[] = [],
    found = LIMIT,
    offset = 0;

  let windowStart: Date;
  switch (window) {
    case MonitoringWindow.WEEK:
      windowStart = startOfWeek(new Date(), { weekStartsOn: 1 });
      break;
    case MonitoringWindow.MONTH:
      windowStart = startOfMonth(new Date());
      break;
    default:
    case MonitoringWindow.YEAR:
      windowStart = startOfYear(new Date());
  }

  const windowString = createWindowString(
    formatDate(windowStart),
    formatDate(new Date()),
  );

  while (found === LIMIT) {
    const { data } = await jobOccupancyApi.v1MeJobOccupancyGet({
      type: window,
      limit: LIMIT,
      offset: LIMIT * offset,
    });
    found = data.items?.length ?? 0;
    items = items.concat(data.items ?? []);
    offset += 1;
  }
  const jobs = items.sort((a: any, b: any) => b.duration - a.duration);
  const startDate = new Date(),
    endDate = new Date();
  endDate.setTime(
    startDate.getTime() + (jobs[0]?.duration || 0) * 60 * 60 * 1000,
  );
  const [distanceNumber, distanceUnit] = formatDistanceStrict(
    startDate,
    endDate,
  ).split(" ");

  const totalConsumption = items.reduce(
    (total: { cpu: number; gpu: number }, item: any) => {
      if (item.cpu) {
        total.cpu += item.cpu;
      }
      if (item.gpu) {
        total.gpu += item.gpu;
      }
      return total;
    },
    { cpu: 0, gpu: 0 },
  );

  const jobsSuffix = items.length > 1 ? "Jobs" : "Job";
  const results: Stat[] = [
    {
      subtitle: ``,
      titleSuffix: jobsSuffix,
      title: numeral(items.length).format("0a"),
      tooltip: `${items.length.toString()} ${jobsSuffix}`,
    },
    {
      subtitle: `Longest Running Job`,
      title: numeral(distanceNumber).format("0a"),
      titleSuffix: distanceUnit,
      tooltip: `${distanceNumber} ${distanceUnit}`,
    },
    {
      subtitle: "CPU Hours",
      title: numeral(totalConsumption.cpu).format("0.0a"),
      tooltip: `${totalConsumption.cpu.toFixed(3)} CPU Hours`,
    },
    {
      subtitle: "GPU Hours",
      title: numeral(totalConsumption.gpu).format("0.0a"),
      tooltip: `${totalConsumption.gpu.toFixed(3)} CPU Hours`,
    },
  ];

  return { statistics: results, windowString };
}
